import React from 'react';
import { twJoin } from 'tailwind-merge';
import { SelectedCheckBadge } from '@/ui/components/pages/ProductDetail/selected-check-badge/selected-check-badge';

export interface BundleSlotTileOption {
  key: string;
  label: string;
  content: React.ReactNode;
}

export interface BundleSlotTilesProps {
  options: BundleSlotTileOption[];
  selectedKey?: string;
  onSelect: (key: string) => void;
  // Id of the product heading that names the group.
  labelledBy: string;
}

// The 50 grey is the lightest neutral whose border still reaches 3:1 (3.95:1) on white.
const UNSELECTED_TILE = 'border border-neutral-50 bg-white hover:border-neutral-70';
// Figma marks the chosen tile with a 1.5 px border; Tailwind has no token for it.
const SELECTED_TILE = 'border-[1.5px] border-neutral-100 bg-primary-5';

/**
 * Wide option tiles of one product of the set on the bundle page. Colour and
 * cylinder slots share the same tile; only the content inside differs.
 */
export function BundleSlotTiles({
  options,
  selectedKey,
  onSelect,
  labelledBy,
}: Readonly<BundleSlotTilesProps>) {
  return (
    // A fieldset groups the tiles natively; min-w-0 lets it shrink inside the grid column.
    <fieldset aria-labelledby={labelledBy} className="grid min-w-0 grid-cols-2 gap-5">
      {options.map(({ key, label, content }) => {
        const isSelected = key === selectedKey;

        return (
          <div key={key}>
            <button
              type="button"
              onClick={() => onSelect(key)}
              aria-pressed={isSelected}
              aria-label={label}
              className={twJoin(
                'relative flex h-[88px] w-full items-center justify-center rounded-lg',
                isSelected ? SELECTED_TILE : UNSELECTED_TILE,
              )}
            >
              {isSelected && <SelectedCheckBadge className="left-3 top-3 h-6 w-6" />}
              {content}
            </button>
            {/* The button already carries the label, so the caption is not read twice. */}
            <div aria-hidden="true" className="mt-3 text-center text-base text-neutral-100">
              {label}
            </div>
          </div>
        );
      })}
    </fieldset>
  );
}
